import { z } from 'zod'
import './registry'
import { MoneySchema } from './common'

/**
 * The single source of truth for how far out a customer hold may reach:
 * caps `customerHoldDefaultDays` here, explicit `heldUntil` dates in the
 * transition handler, and (via `@random-vinyl/backend/schemas`) the frontend
 * date picker's bound. Change it HERE and every enforcement point follows;
 * only the human-readable ERR_HELD_UNTIL_TOO_FAR locale strings repeat the
 * number (the error-text mechanism takes no params).
 */
export const MAX_CUSTOMER_HOLD_DAYS = 30

/**
 * THE single copy of every settings default. Both schema variants below and
 * the exported DEFAULT_TENANT_SETTINGS derive from this object — the wire
 * schema and the read schema sit on OPPOSITE sides of the wire (backend
 * responds via the read variant, frontend parses via the wire variant), so a
 * default that drifted between them would silently disagree across the API
 * with no error and no failing test. The parity test in
 * test/schemas/settings.test.ts pins all three views to this object.
 */
const SETTINGS_DEFAULTS = {
  storeChannelThreshold: '4.00',
  // The client's own figure (2026-10): an AI blurp costs a few cents, mostly
  // web search — not worth it on a record under €4. Admins move it in Instellingen.
  blurpThreshold: '4.00',
  vatStandardRatePercent: 21,
  vatReducedRatePercent: 9,
  customerHoldDefaultDays: 7,
} as const

/**
 * Per-tenant shop settings, persisted as the single `T#<tenantId>` / `META`
 * item. Read by the cataloguing workflow to drive two threshold behaviours,
 * and by (future) VAT reporting for the two configurable rates.
 *
 * The thresholds are money decimal-strings (never JS numbers); the VAT rates
 * are plain 0..100 numbers (same convention as `supplierShareSnapshot`) since
 * they're percentages, not money. A threshold of "0.00" makes the
 * corresponding auto-behaviour inert: no price is `< 0`, so the rule never
 * fires (always blurp / never auto-pick the store channel).
 *
 * TWO variants share the field definitions below:
 *   - `TenantSettingsSchema` (this one, OpenAPI-registered): every field
 *     `.default()`ed so a MISSING key resolves — the strict wire contract.
 *     Server responses are built from already-parsed data, so the frontend's
 *     parse of a response must keep throwing on genuine drift.
 *   - `TenantSettingsReadSchema` (below, NOT registered): additionally
 *     `.catch()`es each field to its default, so a CORRUPT stored value (a
 *     manual edit, a rollback, a future version) is confined to ITS field
 *     instead of failing the whole parse and silently resetting the others.
 *     Used ONLY where raw DynamoDB META items are parsed. Not registered
 *     because zod-to-openapi cannot introspect ZodCatch — which is fine,
 *     since read tolerance is not part of the wire contract anyway.
 *
 * STRIP MODE IS LOAD-BEARING. The handler `safeParse`s this against the whole
 * `META` item, which also carries `name`, `currency`, `boxCapacity`,
 * `fulfilmentEmail`, PK/SK, etc. The explicit `.strip()` drops every key not
 * declared here, so `GET /settings` (readable by non-admins) returns ONLY the
 * fields declared here. Never switch this to `.passthrough()`/`.loose()` —
 * that would leak unrelated tenant config (e.g. `fulfilmentEmail`) to all
 * roles. And don't `.strict()` it: the raw item legitimately has extra keys,
 * which would make every parse fail and silently fall back to defaults.
 */
export const TenantSettingsSchema = z
  .object({
    /**
     * Records priced strictly below this auto-select the `store` sales channel
     * (still overridable by the user). "0.00" disables the auto-selection.
     * Defaulted for the same read-tolerance reason as every other field here:
     * without it, a META item predating this field fails the WHOLE parse and
     * every OTHER configured setting silently reverts to its default on read —
     * which made a freshly PATCHed customerHoldDefaultDays invisible on
     * legacy tenants. No settings field may be default-less.
     */
    storeChannelThreshold: MoneySchema.default(SETTINGS_DEFAULTS.storeChannelThreshold),
    /**
     * Records priced strictly below this do NOT get an AI blurp auto-generated
     * (manual generation stays available, and raising the price on the label
     * screen generates it after all). "0.00" means always auto-generate.
     * Defaulted — see storeChannelThreshold above.
     */
    blurpThreshold: MoneySchema.default(SETTINGS_DEFAULTS.blurpThreshold),
    /**
     * Standard VAT rate (0..100) for this tenant's country — applied to new
     * (sealed) stock and most non-record goods. NL default is 21%. Captured
     * for reporting; not yet computed/shown anywhere (see the VAT scope note
     * on `VatTreatmentSchema` in schemas/transactions.ts). Defaulted so a
     * META item written before this field existed still parses on read
     * (without a default, a pre-existing tenant's whole settings parse would
     * fail and silently discard its configured thresholds too).
     */
    vatStandardRatePercent: z
      .number()
      .min(0)
      .max(100)
      .default(SETTINGS_DEFAULTS.vatStandardRatePercent),
    /**
     * Reduced VAT rate (0..100), e.g. books/food. NL default is 9%. Not
     * currently consumed — the shop has no reduced-rate product type yet —
     * but kept tenant-configurable since rates vary by country. Defaulted for
     * the same read-tolerance reason as vatStandardRatePercent above.
     */
    vatReducedRatePercent: z
      .number()
      .min(0)
      .max(100)
      .default(SETTINGS_DEFAULTS.vatReducedRatePercent),
    /**
     * How many days a customer hold (drawer "Reserveren" without an explicit
     * "vasthouden tot" date) lasts before the expiry sweeper releases the copy
     * back to stock. NO hold is ever indefinite — that policy exists because
     * undated holds silently piled up before it. Capped at 30 (also the cap
     * on explicit dates in the transition handler). Defaulted for the same
     * read-tolerance reason as the VAT rates above.
     */
    customerHoldDefaultDays: z
      .number()
      .int()
      .min(1)
      .max(MAX_CUSTOMER_HOLD_DAYS)
      .default(SETTINGS_DEFAULTS.customerHoldDefaultDays),
  })
  .strip()
  .openapi('TenantSettings')

/**
 * Read-tolerant variant for parsing RAW DynamoDB META items (see the class
 * doc above): each field `.catch()`es its default, so one corrupt stored
 * value never resets its neighbours on read. Same inferred type as
 * TenantSettingsSchema. Consequence: `safeParse` only fails on a non-object
 * input, i.e. a missing META item. Use THIS at every DDB read site; use
 * TenantSettingsSchema for wire contracts.
 */
export const TenantSettingsReadSchema = z
  .object({
    storeChannelThreshold: TenantSettingsSchema.shape.storeChannelThreshold.catch(
      SETTINGS_DEFAULTS.storeChannelThreshold
    ),
    blurpThreshold: TenantSettingsSchema.shape.blurpThreshold.catch(
      SETTINGS_DEFAULTS.blurpThreshold
    ),
    vatStandardRatePercent: TenantSettingsSchema.shape.vatStandardRatePercent.catch(
      SETTINGS_DEFAULTS.vatStandardRatePercent
    ),
    vatReducedRatePercent: TenantSettingsSchema.shape.vatReducedRatePercent.catch(
      SETTINGS_DEFAULTS.vatReducedRatePercent
    ),
    customerHoldDefaultDays: TenantSettingsSchema.shape.customerHoldDefaultDays.catch(
      SETTINGS_DEFAULTS.customerHoldDefaultDays
    ),
  })
  .strip()

/**
 * PATCH /settings body — a partial update of the tenant settings, admin-only
 * (enforced in the handler). Every field optional, same per-field validation
 * as the entity schema, `.strict()` so an unknown/mistyped key is a 400
 * rather than a silent drop. The handler rejects an empty body
 * (ERR_EMPTY_UPDATE). NOTE: deliberately built field-by-field, not via
 * `.partial()` on TenantSettingsSchema — `.partial()` does NOT suppress the
 * entity's `.default()`s (same trap documented on UpdateRecordRequestSchema),
 * so an empty PATCH would silently re-assert every defaulted rate.
 */
export const UpdateTenantSettingsRequestSchema = z
  .object({
    storeChannelThreshold: MoneySchema.optional(),
    blurpThreshold: MoneySchema.optional(),
    vatStandardRatePercent: z.number().min(0).max(100).optional(),
    vatReducedRatePercent: z.number().min(0).max(100).optional(),
    customerHoldDefaultDays: z.number().int().min(1).max(MAX_CUSTOMER_HOLD_DAYS).optional(),
  })
  .strict()
  .openapi('UpdateTenantSettingsRequest')
export type UpdateTenantSettingsRequest = z.infer<typeof UpdateTenantSettingsRequestSchema>

/**
 * Defaults applied when a tenant has no `#META` item yet (freshly provisioned)
 * or it predates the settings fields. Kept here so the handler and the
 * provisioning script share one source of truth.
 */
export const DEFAULT_TENANT_SETTINGS: TenantSettings = SETTINGS_DEFAULTS

export type TenantSettings = z.infer<typeof TenantSettingsSchema>
